import { useCallback, useEffect, useMemo } from 'react';
import { useReleaseNotes } from './useReleaseNotes';
import { useWhatsNewReadStore } from '../stores/whatsNewReadStore';
import { seedReadState, selectUnread } from '../utils/releaseNotes';

/**
 * Joins the served releases to the browser's read state (D1 / R1 — per
 * browser, not per user) and runs the first-run back-fill exactly once.
 *
 * Every surface that needs "is there anything new" reads this, so the seeding
 * rule lives in one place instead of being re-derived by the badge and the
 * drawer independently.
 */
export const useWhatsNewUnread = () => {
  const { data: releases = [], isSuccess } = useReleaseNotes();
  const readVersions = useWhatsNewReadStore((s) => s.readVersions);
  const seeded = useWhatsNewReadStore((s) => s.seeded);
  const seed = useWhatsNewReadStore((s) => s.seed);
  const markRead = useWhatsNewReadStore((s) => s.markRead);

  // Back-fill on the first successful load only. Gated on `isSuccess` rather
  // than on a non-empty list: seeding from an empty list would set `seeded`
  // against a payload that never arrived, and the real first release would then
  // never be back-filled.
  useEffect(() => {
    if (!isSuccess || seeded || releases.length === 0) return;
    seed(seedReadState(releases));
  }, [isSuccess, seeded, releases, seed]);

  const unread = useMemo(
    // Until the seed has run, everything would look unread — report nothing
    // rather than briefly flashing a badge counting the whole back catalogue.
    () => (seeded ? selectUnread(releases, readVersions) : []),
    [seeded, releases, readVersions],
  );

  // Stable across the re-render that marking itself causes. `markRead` always
  // writes a fresh array, so an unmemoised closure here would re-run any effect
  // that calls it on mount, which marks again, which re-renders — a loop.
  const markAllRead = useCallback(
    () => markRead(releases.map((r) => r.version)),
    [releases, markRead],
  );

  return { releases, unread, unreadCount: unread.length, markAllRead, isReady: isSuccess };
};

export default useWhatsNewUnread;
